import React, { useState } from 'react';
import {
    Box, Typography, Divider, Avatar, ButtonBase, ListItemIcon, ListItemText,
    useTheme, useMediaQuery, Drawer, IconButton
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import EmailIcon from '@mui/icons-material/Email';
import ForumIcon from '@mui/icons-material/Forum';
import PaymentIcon from '@mui/icons-material/Payment';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from './AuthContext';
import StaffSelector from './StaffSelector';

interface SidebarItemProps {
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
  active: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ icon, text, onClick, active }) => {
    const theme = useTheme();
    return (
        <ButtonBase
            onClick={onClick}
            sx={{
                width: '100%',
                textAlign: 'left',
                justifyContent: 'flex-start',
                px: 3,
                py: 1.5,
                borderRadius: '0 50px 50px 0',
                mb: 0.5,
                transition: 'all 0.3s ease',
                backgroundColor: active ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                color: active ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: '#fff',
                },
                position: 'relative',
                overflow: 'hidden',
                '&::before': active ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    backgroundColor: theme.palette.primary.main,
                } : {},
            }}
        >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                {icon}
            </ListItemIcon>
            <ListItemText primary={text} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }} />
        </ButtonBase>
    );
};

const SIDEBAR_WIDTH = 280;

const sidebarBackground = 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)';

/** サイドバー中身（デスクトップ・モバイル共通） */
const SidebarContent: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth() as any;

    const isActive = (path: string): boolean => location.pathname === path;

    const go = (path: string) => {
        navigate(path);
        onNavigate?.();
    };

    return (
        <Box sx={{
            width: SIDEBAR_WIDTH,
            background: sidebarBackground,
            display: 'flex',
            flexDirection: 'column',
            pt: 4,
            pb: 2,
            height: '100%',
        }}>
            <Box sx={{ px: 3, mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, letterSpacing: -0.5 }}>
                    レンタカー予約カレンダーUI
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Management System v1.2
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <SidebarItem icon={<DashboardIcon />} text="ダッシュボード" onClick={() => go('/dashboard')} active={isActive('/dashboard')} />
                <SidebarItem icon={<CalendarMonthIcon />} text="カレンダー" onClick={() => go('/calendar')} active={isActive('/calendar')} />
                <SidebarItem icon={<FormatListBulletedIcon />} text="予約一覧" onClick={() => go('/reservations')} active={isActive('/reservations')} />
                <SidebarItem icon={<ListAltIcon />} text="貸出返却リスト" onClick={() => go('/daylist')} active={isActive('/daylist')} />
                <SidebarItem icon={<DirectionsCarIcon />} text="車両リスト" onClick={() => go('/vehicle_list')} active={isActive('/vehicle_list')} />

                <Box sx={{ px: 3, mt: 4, mb: 1 }}>
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 700 }}>
                        ツール
                    </Typography>
                </Box>
                <SidebarItem icon={<InventoryIcon />} text="在庫管理" onClick={() => go('/stock')} active={isActive('/stock')} />
                <SidebarItem icon={<EmailIcon />} text="メール一斉送信" onClick={() => go('/main_send')} active={isActive('/main_send')} />
                <SidebarItem icon={<PaymentIcon />} text="決済確認" onClick={() => go('/payment_check')} active={isActive('/payment_check')} />
                <SidebarItem icon={<ForumIcon />} text="LINEチャット" onClick={() => go('/line')} active={isActive('/line')} />
            </Box>

            <Box sx={{ mt: 'auto', px: 3, pt: 2 }}>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                        <AccountCircleIcon fontSize="small" />
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem' }}>
                            {user?.username}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}>
                            ログイン中
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <StaffSelector user={user} fullWidth sx={{
                        background: 'rgba(255,255,255,0.05)',
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '& .MuiSelect-select': { color: '#fff', fontSize: '0.85rem' }
                    }} />
                </Box>
                <ButtonBase
                    onClick={logout}
                    sx={{
                        width: '100%',
                        py: 1,
                        borderRadius: 2,
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '0.8rem',
                        '&:hover': { color: '#ef4444' }
                    }}
                >
                    ログアウト
                </ButtonBase>
            </Box>
        </Box>
    );
};

const DashboardSidebar: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [drawerOpen, setDrawerOpen] = useState(false);

    if (!isMobile) {
        // デスクトップ: 固定サイドバー
        return (
            <Box sx={{
                width: SIDEBAR_WIDTH,
                flexShrink: 0,
                position: 'sticky',
                top: 0,
                height: '100vh',
                zIndex: 10,
            }}>
                <SidebarContent />
            </Box>
        );
    }

    // モバイル: ハンバーガーボタン + ドロワー
    return (
        <>
            <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                    position: 'fixed',
                    top: 8,
                    left: 8,
                    zIndex: 1200,
                    bgcolor: '#1e293b',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    '&:hover': { bgcolor: '#334155' },
                    width: 40,
                    height: 40,
                }}
            >
                <MenuIcon />
            </IconButton>
            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: { width: SIDEBAR_WIDTH, background: 'transparent', border: 'none' },
                }}
            >
                <SidebarContent onNavigate={() => setDrawerOpen(false)} />
            </Drawer>
        </>
    );
};

export default DashboardSidebar;
